import { useEffect, useRef, useState } from "react";

/**
 * CsvInput
 * A controlled-ish text input for comma-separated arrays that lets users
 * type spaces, trailing commas, and multi-word entries naturally.
 *
 * - `value` prop is the underlying array (e.g. ["Downtown Dubai", "DIFC"]).
 * - `onChange(nextArray)` fires on every keystroke with the parsed array.
 * - We keep a local raw text state so trailing whitespace / empty tokens
 *   aren't stripped mid-typing (which used to eat spaces and commas).
 * - On blur, we canonicalise the display to "a, b, c".
 */
export default function CsvInput({
  value,
  onChange,
  className,
  placeholder,
  "data-testid": testId,
}) {
  const toText = (arr) => (Array.isArray(arr) ? arr.join(", ") : String(arr || ""));
  const parse = (s) =>
    String(s || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

  const [text, setText] = useState(() => toText(value));
  const focused = useRef(false);

  // Keep local text in sync when the parent value changes and we're NOT
  // actively editing (e.g. initial load / external reset).
  useEffect(() => {
    if (focused.current) return;
    const parentText = toText(value);
    if (parentText !== text) setText(parentText);
  }, [value]);

  return (
    <input
      className={className}
      placeholder={placeholder}
      data-testid={testId}
      value={text}
      onFocus={() => {
        focused.current = true;
      }}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        onChange(parse(next));
      }}
      onBlur={() => {
        focused.current = false;
        const arr = parse(text);
        setText(arr.join(", "));
        onChange(arr);
      }}
    />
  );
}
